import { Link } from "react-router-dom";
import { Navigation } from "@/components/ui/navigation";
import { Button } from "@/components/ui/button";
import { Info, CheckCircle2 } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";

const whyCards = [
	{
		n: 1,
		title: "Revise what matters",
		subtitle: "cut the noise",
		body: "Syllabus‑aligned practice sets keep you focused on the topics that actually appear in exams.",
		quotes: [
			"“Finally know exactly what to study.”",
			"“No more guessing what the examiner wants.”",
		],
	},
	{
		n: 2,
		title: "Practice with purpose",
		subtitle: "track your progress",
		body: "Exam‑style questions with step‑by‑step solutions show how marks are awarded and where to improve.",
		quotes: ["“The checkpoints make revision feel achievable.”"],
	},
	{
		n: 3,
		title: "Improve every attempt",
		subtitle: "answers that teach",
		body: "Model answers and examiner tips explain the ‘why’, so each practice session builds real understanding.",
		quotes: ["“Each try gets me closer to full marks.”"],
	},
];

type HowStepMedia =
	| { type: "video"; src: string; poster?: string; alt?: string }
	| { type: "gif" | "image"; src: string; alt?: string };

type HowStep = {
	n: number;
	title: string;
	subtitle: string;
	body: string;
	media?: HowStepMedia;
};

const howSteps: HowStep[] = [
	{
		n: 1,
		title: "Add Your Subjects",
		subtitle: "build your dashboard",
		body: "Pick the AP classes you’re taking so your practice progress is organized in one place.",
		media: {
			type: "gif",
			src: "/media/how-it-works/add-your-subjects.gif",
			alt: "Looped walkthrough of adding AP subjects to the dashboard.",
		},
	},
	{
		n: 2,
		title: "Start Practicing",
		subtitle: "build confidence",
		body: "Create a focused question set by unit and topic, then learn from instant feedback and explanations.",
		media: {
			type: "gif",
			src: "/media/how-it-works/select-study-tool.gif",
			alt: "Animated clip showing how to start a practice set from a subject page.",
		},
	},
	{
		n: 3,
		title: "Get that 5",
		subtitle: "execute the plan",
		body: "Track progress, focus on weak areas, and practice smart — repeat until exam day confidence is locked in.",
		media: {
			type: "gif",
			src: "/media/how-it-works/track-progress.gif",
			alt: "Animated clip highlighting dashboard progress and recent activity.",
		},
	},
];

const apClasses = [
	"AP Biology",
	"AP Chemistry",
	"AP Physics 1",
	"AP Physics 2",
	"AP Physics C: Mechanics",
	"AP Physics C: E&M",
	"AP Calculus AB",
	"AP Calculus BC",
	"AP Statistics",
	"AP Computer Science A",
	"AP Computer Science Principles",
	"AP Psychology",
	"AP English Language",
	"AP English Literature",
	"AP U.S. History",
	"AP World History",
	"AP Microeconomics",
	"AP Macroeconomics",
	"AP U.S. Government & Politics",
	"AP Human Geography",
	"AP Environmental Science",
];

export default function Home() {
	// Footer subject categories linking to filtered Subjects page
	const subjectCategories = [
		{ id: "history", label: "History" },
		{ id: "economics", label: "Economics" },
		{ id: "science", label: "Science" },
		{ id: "math", label: "Mathematics" },
		{ id: "social", label: "Social Studies" },
	];
	return (
		<div className="min-h-screen bg-soft-gradient text-neutral-900">
			<Navigation />

			<main id="main" className="flex flex-col">
				{/* Announcement banner removed per request */}

				{/* Hero with tighter spacing */}
				<section
					aria-labelledby="hero-title"
					className="relative pt-6 pb-8 sm:pt-8 sm:pb-10"
				>
					<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
						{/* Trust badge */}
						<div className="flex justify-center">
							<div className="inline-flex items-center gap-2 rounded-full ring-1 ring-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-800">
								<CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
								<span className="font-semibold">Trusted by students and teachers</span>
							</div>
						</div>

						<div className="mt-5 text-center space-y-4">
							<h1
								id="hero-title"
								className="mx-auto max-w-[18ch] text-3xl sm:text-4xl lg:text-[40px] leading-tight font-extrabold tracking-tight"
							>
								Exam‑style questions that actually help
							</h1>
							<p className=" mx-auto max-w-[60ch] text-neutral-700 text-base sm:text-lg">
								Targeted, syllabus‑aligned practice — so you learn what earns marks and waste zero time.
							</p>
							<div className=" mt-3 flex flex-col sm:flex-row items-center justify-center gap-3">
								<Button
									asChild
									className="rounded-full px-6 sm:px-7 py-2.5 text-base h-auto"
									data-analytics-id="cta-start"
								>
									<Link to="/auth">Start free</Link>
								</Button>
							</div>
						</div>
					</div>
				</section>

				{/* Why it works */}
				<section aria-labelledby="why-title" className="py-8 sm:py-10 lg:py-14">
					<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
						<h2
							id="why-title"
							className="text-center text-2xl sm:text-3xl font-extrabold tracking-tight"
						>
							Why it works
						</h2>

						<div className="mt-8 grid gap-6 sm:gap-7 md:grid-cols-2 lg:grid-cols-3">
							{whyCards.map((item) => (
								<article
									key={item.n}
									className="relative rounded-2xl border border-neutral-200 bg-white/90 backdrop-blur-sm shadow-[0_6px_24px_rgba(0,0,0,0.06)]"
								>
									<div className="p-6 sm:p-7">
										<div className="flex items-center gap-3">
											<span
												className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-sm font-bold"
												aria-hidden="true"
											>
												{item.n}
											</span>
											<div>
												<h3 className="text-lg font-bold">{item.title}</h3>
												<p className="text-sm text-neutral-500 -mt-0.5">{item.subtitle}</p>
											</div>
										</div>

										<hr className="my-4 border-blue-200" />

										<p className="text-neutral-800 leading-7">{item.body}</p>

										<div className="mt-4 space-y-2">
											{item.quotes.map((q, idx) => (
												<p key={idx} className="quote-chip">
													{q}
												</p>
											))}
										</div>
									</div>
								</article>
							))}
						</div>
					</div>
				</section>

				{/* How it works */}
				<section aria-labelledby="how-title" className="py-6 sm:py-8 lg:py-10">
					<h2
						id="how-title"
						className="text-center text-2xl sm:text-3xl font-extrabold tracking-tight"
					>
						How it works
					</h2>

					<div className="mt-6 space-y-8 sm:space-y-10 lg:space-y-12">
						{howSteps.map((item, idx) => {
							const isEven = idx % 2 === 0;
							// Soft alternating backgrounds
							const bg =
								idx === 0 ? "bg-violet-50/70"
								: idx === 1 ? "bg-emerald-50/70"
								: "bg-blue-50/70";
							const media = item.media;
							const isVideo = media?.type === "video" && Boolean(media.src);
							const isImage = (media?.type === "image" || media?.type === "gif") && Boolean(media.src);
							const fallbackMediaPath =
								media?.src ??
								`/media/how-it-works/step-${item.n}.${media?.type === "gif" ? "gif"
								: media?.type === "image" ? "jpg" : "mp4"}`
							return (
								<div key={item.n} className={`w-full border-y border-neutral-100 ${bg}`}>
									<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
										<div className="grid items-center gap-8 lg:grid-cols-12">
											{/* Media */}
											<div className={`lg:col-span-6 ${isEven ? "" : "lg:order-2"}`}>
												<AspectRatio ratio={16 / 9}>
													{isVideo ? (
														<video
															controls
															playsInline
															preload="metadata"
															className="h-full w-full rounded-2xl border border-neutral-200 bg-black object-cover shadow-[0_6px_24px_rgba(0,0,0,0.06)]"
															poster={media && "poster" in media ? media.poster : undefined}
															aria-label={media?.alt ?? item.title}
														>
															<source src={media?.src} type="video/mp4" />
															Your browser does not support embedded videos.
														</video>
													) : isImage ? (
														<img
															src={media?.src}
															alt={media?.alt ?? item.title}
															className="h-full w-full rounded-2xl border border-neutral-200 object-cover shadow-[0_6px_24px_rgba(0,0,0,0.06)]"
															loading="lazy"
														/>
													) : (
														<div className="w-full h-full rounded-2xl border border-neutral-200 bg-white/90 flex items-center justify-center text-center px-4 text-neutral-500 text-sm shadow-[0_6px_24px_rgba(0,0,0,0.06)]">
															{media?.type === "gif"
																? `Upload a GIF to ${fallbackMediaPath} to display it here.`
																: `Upload media to ${fallbackMediaPath} to display it here.`}
														</div>
													)}
												</AspectRatio>
											</div>
											{/* Text */}
											<div className={`lg:col-span-6 ${isEven ? "" : "lg:order-1"}`}>
												<div className="flex items-center gap-3">
													<span
														className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white text-sm font-bold"
														aria-hidden="true"
													>
														{item.n}
													</span>
													<div>
														<h3 className="text-2xl font-extrabold tracking-tight">{item.title}</h3>
														<p className="text-sm text-neutral-600 -mt-0.5">{item.subtitle}</p>
													</div>
												</div>
												<p className="mt-3 text-neutral-800 leading-7">{item.body}</p>
											</div>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</section>

				{/* Get started panel */}
				<section aria-labelledby="get-started-title" className="py-4 sm:py-8 lg:py-10">
					<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
						<div className="rounded-[28px] bg-white/70 backdrop-blur-sm border border-neutral-100 px-4 sm:px-8 py-8 sm:py-10">
							<div className="text-center space-y-2">
								<p className="text-xs tracking-[0.08em] font-semibold text-neutral-600">
									EXPLORE AP CLASSES
								</p>
								<h2
									id="get-started-title"
									className="text-2xl sm:text-3xl font-extrabold tracking-tight"
								>
									Get started — free
								</h2>
							</div>

							<div className="mt-6">
								<div
									className="marquee"
									role="region"
									aria-label="AP classes carousel"
								>
									<div className="marquee__track" role="list" aria-live="polite">
										{[...apClasses, ...apClasses].map((label, i) => (
											<a
												key={i}
												href="#"
												role="listitem"
												className="level-pill"
												data-analytics-id={`ap-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
											>
												{label}
											</a>
										))}
									</div>
								</div>
							</div>
						</div>
					</div>
				</section>
			</main>

			{/* Footer */}
			<footer role="contentinfo" className="mt-10 border-t border-neutral-200">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
					<div className="grid gap-8">
						<div className="col-span-1">
							<div className="flex items-center gap-2">
								<span className="font-semibold">Lucen</span>
							</div>
						</div>

						<div className="mx-auto w-full lg:w-[92%] xl:w-[90%] grid grid-cols-1 sm:grid-cols-3 items-start justify-items-center gap-12 lg:gap-28 xl:gap-40">
							<nav aria-label="Resources" className="space-y-2">
								<h3 className="text-sm font-semibold text-neutral-600">Resources</h3>
								<ul className="space-y-2">
									<li><Link className="footer-link" to="/subjects">Subjects</Link></li>
									<li><Link className="footer-link" to="/dashboard">Dashboard</Link></li>
								</ul>
							</nav>

							<nav aria-label="Company" className="space-y-2">
								<h3 className="text-sm font-semibold text-neutral-600">Company</h3>
								<ul className="space-y-2">
									<li><Link className="footer-link" to="/about">About Us</Link></li>
									<li><Link className="footer-link" to="/terms-privacy">Terms & Privacy</Link></li>
									<li><Link className="footer-link" to="/report-issue">Report an Issue</Link></li>
									<li><Link className="footer-link" to="/sources">Sources</Link></li>
								</ul>
							</nav>

							<nav aria-label="Subjects" className="space-y-2">
								<h3 className="text-sm font-semibold text-neutral-600">Subjects</h3>
								<ul className="space-y-2">
									{subjectCategories.map((c) => (
										<li key={c.id}>
											<Link className="footer-link" to={`/subjects?category=${c.id}`}>
												{c.label}
											</Link>
										</li>
									))}
								</ul>
							</nav>
						</div>
					</div>

					<div className="mt-8 text-center text-sm text-neutral-500">
						<p>© 2026 Lucen — All rights reserved.</p>
					</div>
				</div>
			</footer>
		</div>
	);
}